/**
 * TrialBanner — surfaces the active trial state on BillingTab.
 *
 * Two flavours, gated by ``hasPaymentMethod``:
 *
 *   hasPaymentMethod=true  → low-urgency "you're all set" heads-up. No CTA.
 *                            Stripe will auto-charge at trial end; we just
 *                            need to remind the user the bill is coming.
 *   hasPaymentMethod=false → urgency tier-based on ``daysRemaining`` (>3 /
 *                            1–3 / day-of), with an "Add payment method"
 *                            CTA that opens the Stripe portal. Without a
 *                            card on file Stripe flips the sub to
 *                            ``incomplete_expired`` and the customer loses
 *                            access.
 *
 * Cancellation is intentionally NOT surfaced here — the Stripe portal
 * ("Manage billing →" in SubscriptionSection) is the single canonical
 * place to cancel. Surfacing a cancel button next to the trial reminder
 * would create dropoff for customers who are just looking for billing
 * info, not actively trying to leave.
 *
 * The urgency is conveyed in text (not just the icon color) so
 * screen-reader and colorblind users get the same signal as sighted ones.
 *
 * Pairs with PostTrialExpiredState — that handles the post-expiry case
 * where the subscription has flipped to ``incomplete_expired``.
 */

import { Clock, Star05 } from '@untitledui/icons';
import { useMemo } from 'react';

import { StatusBanner, type StatusBannerAction } from '@/lib/base/banners';

interface TrialBannerProps {
  trialEnd: string;
  daysRemaining: number;
  /**
   * True when the customer has a default payment method on file (e.g. the
   * common Stripe Checkout flow which collects the card up front before
   * starting the trial). When true, the banner switches to "we'll charge
   * your card on X" copy with no CTA. When false (rare — admin-granted
   * trial without card), keeps the "Add payment method" CTA.
   */
  hasPaymentMethod: boolean;
  /** Triggered by the "Add payment method" CTA. Only used when !hasPaymentMethod. */
  onAddPaymentMethod: () => void;
  isPortalLoading?: boolean;
}

function formatTrialEnd(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function TrialBanner({
  trialEnd,
  daysRemaining,
  hasPaymentMethod,
  onAddPaymentMethod,
  isPortalLoading,
}: TrialBannerProps) {
  const { variant, icon, title, description } = useMemo(() => {
    const formatted = formatTrialEnd(trialEnd);

    // ---- Has-payment-method path: friendly, no urgency. -----------------
    if (hasPaymentMethod) {
      if (daysRemaining <= 0) {
        return {
          variant: 'info' as const,
          icon: Clock,
          title: 'Your trial ends today.',
          description:
            "We'll charge the card on file to start your subscription. " +
            'No action needed.',
        };
      }
      if (daysRemaining <= 3) {
        const dayWord = daysRemaining === 1 ? 'day' : 'days';
        return {
          variant: 'info' as const,
          icon: Clock,
          title: `Your trial ends in ${daysRemaining} ${dayWord}.`,
          description: `We'll charge the card on file on ${formatted}. No action needed.`,
        };
      }
      return {
        variant: 'info' as const,
        icon: Star05,
        title: `You're on trial until ${formatted}.`,
        description:
          "We'll automatically charge the card on file to start your " +
          'subscription. No action needed until then.',
      };
    }

    // ---- No-payment-method path: original urgency tiers. ----------------
    if (daysRemaining <= 0) {
      // Day-of: highest urgency. Stripe will flip the sub to
      // incomplete_expired at trial_end; we want the user to act now.
      return {
        variant: 'warning' as const,
        icon: Clock,
        title: 'Your trial ends today – add a payment method now.',
        description:
          'Without a payment method on file, access will pause when the trial ends.',
      };
    }

    if (daysRemaining <= 3) {
      // ≤3 days: warning tone + countdown in the headline.
      const dayWord = daysRemaining === 1 ? 'day' : 'days';
      return {
        variant: 'warning' as const,
        icon: Clock,
        title: `Your trial ends in ${daysRemaining} ${dayWord} – add a payment method to avoid losing access.`,
        description: `Trial ends on ${formatted}.`,
      };
    }

    // > 3 days: friendly info-tone heads-up.
    return {
      variant: 'info' as const,
      icon: Star05,
      title: `Your trial ends on ${formatted}.`,
      description:
        'Add a payment method to keep your subscription running once the trial finishes.',
    };
  }, [daysRemaining, trialEnd, hasPaymentMethod]);

  // Only attach the "Add payment method" CTA when the customer hasn't given
  // us a card. With a card on file there's nothing for them to do —
  // surfacing a CTA there is misleading (matches existing UX bug report).
  const action = useMemo<StatusBannerAction | undefined>(() => {
    if (hasPaymentMethod) {
      return undefined;
    }
    return {
      label: 'Add payment method',
      onClick: onAddPaymentMethod,
      isLoading: isPortalLoading,
    };
  }, [hasPaymentMethod, onAddPaymentMethod, isPortalLoading]);

  return (
    <div data-component='billing-banner' data-instance='trial-ending'>
      <StatusBanner
        variant={variant}
        icon={icon}
        title={title}
        description={description}
        action={action}
      />
    </div>
  );
}
